/**
 * Events.ts
 *
 * A module for all Event handling.
 */

import { Transporter } from "./../../Transport/Transport";
import { AutoDismissValues, IDs, Keys } from "./../../Constants";
import * as UiUtils from "./../Utils";
import { IOnDismissDelegate } from "./../../IOnDismissDelegate";
import { Spinner } from "./../SpinnerControl";
import * as Logging from "./../../Logging/Logging";
import * as Configuration from "./../../Configuration/Configuration";
import * as Api from "@ms-ofb/officefloodgatecore/dist/src/Api/Api";

/**
 * Callback for when the feedback dialog is dismissed
 */
let onDismiss: IOnDismissDelegate;

/**
 * Is the prompt up
 */
let isPromptUp: boolean;

/**
 * Dismiss all, including networking, UI, and events
 * @param submitted Was the control submitted (true), or cancelled (false)?
 */
function dismissAll(submitted: boolean): void {
	unregister();
	onDismiss(submitted);
}

/**
 * Listener un-registration
 */
function unregister(): void {
	removeListeners();
}

/**
 * List of listeners and the ids they are attached to. This ensures all events are correctly unregistered.
 */
let listenersList: { id: string, event: string, listener: (event: Event) => void }[] = [];

function addListener(id: string, event: string, listener: (event: Event) => void): void {
	UiUtils.registerListener(id, event, listener);
	listenersList.push({ event: event, id: id, listener: listener });
}

function removeListeners(): void {
	for (let listener of listenersList) {
		UiUtils.unregisterListener(listener.id, listener.event, listener.listener);
	}
	listenersList = [];
}

/**
 * Register events for toast
 */
export function register(ON_DISMISS: IOnDismissDelegate = function (submitted: boolean) { return; }) {
	onDismiss = ON_DISMISS;
	isPromptUp = true;

	addListener(IDs.ToastContainer, "keyup", toastKeyEventHandler);
	addListener(IDs.ToastCancel, "click", toastCancelHandler);
	addListener(IDs.TPromptContainer, "click", promptContainerHandler);

	addListener(IDs.TFormSubmitButton, "click", submitButtonHandler);

	addListener(IDs.TFormEmailCheckBox, "click", emailCheckBoxHandler);
	emailCheckBoxHandler(null); // call the checkbox handler to process the initial state

	let ratingElements = document.querySelectorAll("input[name=\"" + IDs.TFormRating + "\"]");
	for (let i = 0; i < ratingElements.length; ++i) {
		UiUtils.registerListenerToElement(ratingElements[i] as HTMLElement, "click", ratingInputHandler);
	}

	if (!Configuration.get().getFloodgateSurvey().showPrompt) {
		showSurveyScreen();
	} else {
		Logging.getLogger().logEvent(Logging.EventIds.Survey.UI.Prompt.Shown.VALUE, {
			CampaignId: Configuration.get().getFloodgateSurvey().getCampaignId(),
			SurveyId: Configuration.get().getFloodgateSurvey().getId(),
			SurveyType: Configuration.get().getFloodgateSurvey().getSurveyType(),
		});
	}

	// Auto dismiss if needed
	let autoDimiss = Configuration.get().getFloodgateInitOptions().autoDismiss;
	if (autoDimiss !== AutoDismissValues.NoAutoDismiss) {
		let autoDimissDuration: number;
		switch (autoDimiss) {
			case (AutoDismissValues.SevenSeconds):
				autoDimissDuration = 7000;
				break;
			case (AutoDismissValues.FourteenSeconds):
				autoDimissDuration = 14000;
				break;
			case (AutoDismissValues.TwentyOneSeconds):
				autoDimissDuration = 21000;
				break;
			case (AutoDismissValues.TwentyEightSeconds):
				autoDimissDuration = 28000;
				break;
			default:
		}
		if (autoDimissDuration !== undefined) {
			setTimeout(
				function () {
					if (isPromptUp) { dismissAll(false); }
				},
				autoDimissDuration
			);
		}
	}
}

function emailCheckBoxHandler(event: Event): void {
	// show email if checked and show default string if unchecked
	let emailCheckBox: HTMLInputElement = <HTMLInputElement> document.getElementById(IDs.TFormEmailCheckBox);
	if (emailCheckBox) {
		let emailTextBox: HTMLInputElement = <HTMLInputElement> document.getElementById(IDs.TFormEmailTextBox);
		emailTextBox.value = emailCheckBox.checked ? Configuration.get().getCommonInitOptions().userEmail : null;
		emailTextBox.disabled = !emailCheckBox.checked;
	}
}

function toastKeyEventHandler(event: Event): void {
	// dismiss upon pressing the escape key
	if ((<KeyboardEvent> event).keyCode === Keys.Esc) {
		event.preventDefault();
		event.stopPropagation();

		dismissAll(false);
	}
}

function toastCancelHandler(event: Event): void {
	event.preventDefault();
	event.stopPropagation();

	dismissAll(false);
}

function promptContainerHandler(event: Event): void {
	event.preventDefault();
	event.stopPropagation();

	showSurveyScreen();
}

function showSurveyScreen(): void {
	isPromptUp = false;

	UiUtils.setElementVisibility(IDs.TPromptContainer, false);
	UiUtils.setElementVisibility(IDs.TFormContainer, true);

	Logging.getLogger().logEvent(Logging.EventIds.Survey.UI.Form.Shown.VALUE, {
		CampaignId: Configuration.get().getFloodgateSurvey().getCampaignId(),
		SurveyId: Configuration.get().getFloodgateSurvey().getId(),
		SurveyType: Configuration.get().getFloodgateSurvey().getSurveyType(),
	});
}

function ratingInputHandler(event: Event): void {
	(document.getElementById(IDs.TFormSubmitButton) as HTMLButtonElement).disabled = false;
}

function submitButtonHandler(event: Event): void {
	event.preventDefault();
	event.stopPropagation();

	UiUtils.setElementVisibility(IDs.TFormSubmitButton, false);
	let spinner = new Spinner(IDs.TFormSubmitButtonSpinner);

	let transporter: Transporter = new Transporter(
		Configuration.get().getCommonInitOptions().environment,
		Api.ISurvey.Type[Configuration.get().getFloodgateSurvey().getSurveyType()],
		Configuration.get().getCommonInitOptions().appId,
		"Survey",
		Configuration.get().getCommonInitOptions().applicationGroup,
		Configuration.get().getCommonInitOptions().telemetryGroup,
		Configuration.get().getCommonInitOptions().webGroup,
	);

	let commentElement: HTMLTextAreaElement = <HTMLTextAreaElement> document.getElementById(IDs.TFormComment);
	let commentEntered: boolean = (commentElement && !!commentElement.value);

	let selectedRating: HTMLInputElement =
		document.querySelector("input[name=\"" + IDs.TFormRating + "\"]:checked") as HTMLInputElement;

	Configuration.get().getFloodgateSurvey().setValues(
		Number(selectedRating.value),
		commentEntered ? commentElement.value : ""
	);

	// Add survey-specific data to manifest
	transporter.setManifestValues(Configuration.get().getFloodgateSurvey().getJsonElements());

	// Send email address if user gives consent
	let emailCheckBox: HTMLInputElement = <HTMLInputElement> document.getElementById(IDs.TFormEmailCheckBox);
	let emailTextBox: HTMLInputElement = <HTMLInputElement> document.getElementById(IDs.TFormEmailTextBox);

	if (emailCheckBox && emailCheckBox.checked && emailTextBox.value !== "") {
			transporter.setEmail(emailTextBox.value);
		}

	transporter.submit();
	spinner.destroy();
	dismissAll(true);

	Logging.getLogger().logEvent(Logging.EventIds.Survey.UI.Form.Submit.VALUE, {
		CampaignId: Configuration.get().getFloodgateSurvey().getCampaignId(),
		SurveyId: Configuration.get().getFloodgateSurvey().getId(),
		SurveyType: Configuration.get().getFloodgateSurvey().getSurveyType(),
	});
}
